import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ScreenManualDrawer from './ScreenManualDrawer';
import {
  Home,
  FileText,
  Users,
  Package,
  FolderOpen,
  Settings,
  UserCog,
  FileBarChart,
  LogOut,
  Menu,
  X,
  Ruler,
  DollarSign,
  ClipboardList,
  BookOpen,
  HelpCircle,
  Briefcase,
  UserCheck,
  Variable
} from 'lucide-react';

const Layout = () => {
  const { user, logout, impersonate, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [impersonateOpen, setImpersonateOpen] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [manualOpen, setManualOpen] = useState(false);
  const location = useLocation();

  // Map route paths to screen_keys
  const getScreenKey = () => {
    const path = location.pathname.replace(/^\//, '');
    const pathMap = {
      'dashboard': 'dashboard',
      'documents': 'documentos_lista',
      'documents/new': 'documento_novo',
      'clients': 'clientes_lista',
      'products': 'products',
      'templates': 'templates_lista',
      'opportunities': 'oportunidades_lista',
      'question-bank': 'banco_perguntas',
      'libraries': 'bibliotecas',
      'users': 'usuarios',
      'audit': 'auditoria',
      'settings': 'configuracoes',
      'units': 'unidades_medida',
      'prices': 'prices',
      'my-tasks': 'minhas_tarefas',
      'screen-manuals': 'manuais_tela',
      'variables': 'variables',
      'doc-variables': 'doc_variables',
      'variable-dictionary': 'variable_dictionary',
    };
    if (path.match(/^documents\/[^/]+$/) && path !== 'documents/new') return 'documento_detalhe';
    if (path.match(/^opportunities\/[^/]+$/)) return 'oportunidade_detalhe';
    return pathMap[path] || path.replace(/\//g, '_');
  };

  const isAdmin = user?.role === 'Administrador';
  const isSenior = user?.role === 'Redator Sênior';
  const isRedator = user?.role === 'Redator Padrão';
  const canCadastros = isAdmin || isSenior;
  const isImpersonating = user?.is_impersonation;

  const loadUsersForImpersonate = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
      const res = await (await import('axios')).default.get(`${API_URL}/users`);
      setUsersList(res.data.filter(u => u.user_id !== user?.user_id && u.is_active));
      setImpersonateOpen(true);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const handleImpersonate = async (targetUserId) => {
    try {
      await impersonate(targetUserId);
      setImpersonateOpen(false);
    } catch (error) {
      console.error('Impersonate failed:', error);
    }
  };

  const navSections = [
    {
      title: 'Principal',
      items: [
        { path: '/dashboard', icon: Home, label: 'Dashboard' },
        { path: '/my-tasks', icon: ClipboardList, label: 'Minhas Tarefas' },
        { path: '/opportunities', icon: Briefcase, label: 'Perfil do Cliente' },
        { path: '/documents', icon: FileText, label: 'Documentos' },
      ]
    },
    {
      title: 'Cadastros Gerais',
      roles: ['Administrador', 'Redator Sênior', 'Redator Padrão'],
      items: [
        { path: '/clients', icon: Users, label: 'Clientes' },
        { path: '/products', icon: Package, label: 'Produtos', roles: ['Administrador', 'Redator Sênior'] },
        { path: '/units', icon: Ruler, label: 'Unidades de Medida', roles: ['Administrador', 'Redator Sênior'] },
        { path: '/prices', icon: DollarSign, label: 'Tabela de Preços', roles: ['Administrador', 'Redator Sênior'] },
        { path: '/templates', icon: FolderOpen, label: 'Modelos', roles: ['Administrador', 'Redator Sênior'] },
        { path: '/question-bank', icon: HelpCircle, label: 'Banco de Perguntas', roles: ['Administrador', 'Redator Sênior'] },
        { path: '/libraries', icon: BookOpen, label: 'Bibliotecas' },
      ]
    },
    {
      title: 'Variáveis',
      roles: ['Administrador', 'Redator Sênior'],
      items: [
        { path: '/doc-variables', icon: FileText, label: 'Variáveis de Documento' },
        { path: '/variables', icon: Variable, label: 'Variáveis de Fórmula' },
        { path: '/variable-dictionary', icon: BookOpen, label: 'Dicionário de Variáveis' },
      ]
    },
    {
      title: 'Administração',
      roles: ['Administrador'],
      items: [
        { path: '/users', icon: UserCog, label: 'Usuários' },
        { path: '/settings', icon: Settings, label: 'Configurações' },
        { path: '/audit', icon: FileBarChart, label: 'Auditoria' },
        { path: '/screen-manuals', icon: BookOpen, label: 'Manuais de Tela' },
      ]
    }
  ];

  return (
    <div className="flex h-screen bg-[#F7F7F8]">
      {/* Sidebar Desktop */}
      <aside
        className={`hidden lg:flex flex-col bg-[#1C1C1E] text-white transition-all duration-300 ${
          sidebarOpen ? 'w-60' : 'w-16'
        }`}
      >
        {/* Logo */}
        <div className="h-14 flex items-center px-4 border-b border-[#2C2C2E]">
          {sidebarOpen ? (
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_corporate-vault-3/artifacts/kmfx1msv_marca_asi_company.png"
                alt="ASI"
                className="h-8"
              />
              <span className="font-semibold text-lg">ASI Docs</span>
            </div>
          ) : (
            <div className="flex items-center justify-center w-full">
              <span className="text-[#AF2628] font-bold text-xl">A</span>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-2">
          {navSections.map((section) => {
            if (section.roles && !section.roles.includes(user?.role)) return null;
            const visibleItems = section.items.filter(item => !item.roles || item.roles.includes(user?.role));
            if (visibleItems.length === 0) return null;
            return (
              <div key={section.title} className="mb-5">
                {sidebarOpen && (
                  <div className="px-3 mb-2">
                    <p className="text-[10px] font-semibold text-[#6E6E73] uppercase tracking-widest">{section.title}</p>
                  </div>
                )}
                {!sidebarOpen && <div className="border-t border-[#2C2C2E] mx-2 mb-2" />}
                <div className="space-y-0.5">
                  {visibleItems.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                          isActive
                            ? 'bg-[#2C2C2E] text-white border-l-3 border-[#AF2628]'
                            : 'text-[#E5E5E7] hover:bg-[#2C2C2E]'
                        }`
                      }
                    >
                      <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
                      {sidebarOpen && <span className="text-sm">{item.label}</span>}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        {/* User Section */}
        <div className="border-t border-[#2C2C2E] p-4">
          {sidebarOpen ? (
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-[#AF2628] flex items-center justify-center text-white font-semibold">
                  {user?.name?.charAt(0) || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                  <p className="text-xs text-[#6E6E73] truncate">{user?.role}</p>
                </div>
              </div>
              <button
                onClick={logout}
                data-testid="logout-button"
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#E5E5E7] hover:bg-[#2C2C2E] rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Sair</span>
              </button>
              {isAdmin && !isImpersonating && (
                <button
                  onClick={loadUsersForImpersonate}
                  data-testid="impersonate-button"
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-amber-400 hover:bg-[#2C2C2E] rounded-lg transition-colors mt-1"
                >
                  <UserCheck className="w-4 h-4" />
                  <span>Alternar Perfil</span>
                </button>
              )}
            </div>
          ) : (
            <button
              onClick={logout}
              className="w-full flex items-center justify-center p-2 text-[#E5E5E7] hover:bg-[#2C2C2E] rounded-lg"
            >
              <LogOut className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Toggle Button */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="absolute -right-3 top-20 bg-white border border-[#E5E5EA] rounded-full p-1.5 shadow-md hover:bg-[#F7F7F8]"
        >
          {sidebarOpen ? (
            <X className="w-4 h-4 text-[#6E6E73]" />
          ) : (
            <Menu className="w-4 h-4 text-[#6E6E73]" />
          )}
        </button>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Impersonation Banner */}
        {isImpersonating && (
          <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-between text-sm" data-testid="impersonation-banner">
            <div className="flex items-center gap-2">
              <UserCheck className="w-4 h-4" />
              <span>Visualizando como: <strong>{user?.name}</strong> ({user?.role})</span>
            </div>
            <button
              onClick={stopImpersonation}
              data-testid="stop-impersonation-btn"
              className="bg-white text-amber-600 px-3 py-1 rounded-md text-xs font-semibold hover:bg-amber-50 transition-colors"
            >
              Voltar ao Admin
            </button>
          </div>
        )}

        {/* Topbar */}
        <header className="h-14 bg-white border-b border-[#E5E5EA] flex items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 hover:bg-[#F7F7F8] rounded-lg"
            >
              <Menu className="w-5 h-5 text-[#6E6E73]" />
            </button>
            <div className="text-sm text-[#6E6E73]">
              {/* Breadcrumb will be added here */}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setManualOpen(true)}
              data-testid="open-manual-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#6E6E73] hover:text-[#AF2628] hover:bg-[#FFF1F0] rounded-lg transition-colors border border-transparent hover:border-[#F0D0D0]"
              title="Manual da Tela"
            >
              <HelpCircle className="w-4 h-4" />
              <span className="hidden sm:inline">Manual</span>
            </button>
            <div className="hidden md:block text-right">
              <p className="text-sm font-medium text-[#111111]">{user?.name}</p>
              <p className="text-xs text-[#6E6E73]">{user?.role}</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-[#AF2628] flex items-center justify-center text-white font-semibold">
              {user?.name?.charAt(0) || 'U'}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        >
          <div
            className="w-64 h-full bg-[#1C1C1E] text-white p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_corporate-vault-3/artifacts/kmfx1msv_marca_asi_company.png"
                alt="ASI"
                className="h-8"
              />
            </div>
            <nav className="space-y-1">
              {navSections.map((section) => {
                if (section.roles && !section.roles.includes(user?.role)) return null;
                const visibleItems = section.items.filter(item => !item.roles || item.roles.includes(user?.role));
                if (visibleItems.length === 0) return null;
                return (
                  <div key={section.title} className="mb-4">
                    <p className="text-[10px] font-semibold text-[#6E6E73] uppercase tracking-widest px-3 mb-1.5">{section.title}</p>
                    {visibleItems.map((item) => (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        onClick={() => setMobileMenuOpen(false)}
                        data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2.5 rounded-lg ${
                            isActive ? 'bg-[#2C2C2E] text-white' : 'text-[#E5E5E7]'
                          }`
                        }
                      >
                        <item.icon className="w-[18px] h-[18px]" />
                        <span className="text-sm">{item.label}</span>
                      </NavLink>
                    ))}
                  </div>
                );
              })}
            </nav>
          </div>
        </div>
      )}

      {/* Impersonate Modal */}
      {impersonateOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setImpersonateOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()} data-testid="impersonate-modal">
            <div className="px-6 py-4 border-b border-[#E5E5EA]">
              <h3 className="text-lg font-semibold text-[#111111]">Alternar Perfil</h3>
              <p className="text-sm text-[#6E6E73] mt-1">Selecione um usuário para visualizar a plataforma como ele</p>
            </div>
            <div className="p-6 max-h-80 overflow-y-auto space-y-2">
              {usersList.map((u) => (
                <button
                  key={u.user_id}
                  onClick={() => handleImpersonate(u.user_id)}
                  data-testid={`impersonate-user-${u.user_id}`}
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-[#F7F7F8] border border-transparent hover:border-[#E5E5EA] transition-all text-left"
                >
                  <div className="w-9 h-9 rounded-full bg-[#2B5C8A] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                    {u.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-[#111111] truncate">{u.name}</p>
                    <p className="text-xs text-[#6E6E73]">{u.role} &middot; {u.email}</p>
                  </div>
                </button>
              ))}
              {usersList.length === 0 && (
                <p className="text-sm text-[#AEAEB2] text-center py-4">Nenhum usuário disponível</p>
              )}
            </div>
            <div className="px-6 py-3 border-t border-[#E5E5EA]">
              <button
                onClick={() => setImpersonateOpen(false)}
                className="w-full py-2 text-sm text-[#6E6E73] hover:text-[#111111] transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      <ScreenManualDrawer screenKey={getScreenKey()} isOpen={manualOpen} onClose={() => setManualOpen(false)} />
    </div>
  );
};

export default Layout;
